import {Component, ComponentChild, h} from 'preact'
import QuickLinksManager from '../quick-links/QuickLinksManager'
import KnowledgeBaseHomepageManager from '../knowledge-base/control/KnowledgeBaseHomepageManager'
import {SortableContainer, SortableElement} from 'react-sortable-hoc'
import DragHandle from '../control/DragHandle'
import TabsConfigurator, {Tab} from '../homepage-tabs/TabsConfigurator'
import Hero from '../hero'
import Header from '../header'
import { FeaturedTopicsModeratorTags } from './FeaturedTopicsModeratorTags'
import FeaturedTopics from '../featured-topics/FeaturedTopics'
import CommunityHeader from './CommunityHeader'
import NavigationBar from './NavigationBar'
import StatisticsBar from './StatisticsBar'
import IntroductionBar from './IntroductionBar'
import HTMLWidget from '../html-widget/HTMLWidget'
import CommunityFooter from './Footer'
import HomepageContentCurationManager from '../content-curation/HomepageContentCurationManager'
import Switcher from '../control/Switcher'
import { getId } from './Wrapper'
import {TimeRanges} from '../widget-points-leaderboard/indexWidget'

interface State {
  allItems: Array<HomepageElement>
  isLoading: boolean
  hasChanged: boolean
}

export interface HomepageElementInterface {
  id: number
  active: boolean
  nonConfigurable?: boolean
  title?: string
}

interface HTMLHomepageElement extends HomepageElementInterface {
  type: 'html_widget'
  title: string,
  config: {
    html: string
  }
}

export enum GenericHomepageElementTypes {
  header = 'header',
  navigation_bar = 'navigation_bar',
  statistics_bar = 'statistics_bar',
  introduction_bar = 'introduction_bar',
  feature_topics = 'feature_topics',
  announcement_card = 'announcement_card',
  footer = 'footer'
}

interface GenericHomepageElement extends HomepageElementInterface {
  type: GenericHomepageElementTypes
  config: {}
}

export interface HeroBannerHomepageElement extends HomepageElementInterface {
  type: 'hero_banner'
  config: {
    heroImage: string
    headline: string
  }
}

export interface FeaturedTopicsElement extends HomepageElementInterface {
  type: 'featured_topics'
  title: string
  config: {
    topics: number[]
    firstTopicsNumber: number
    buttonLabel: string
    buttonURL: string
  }
}

export interface RecommendationsHomepageElement extends HomepageElementInterface {
  type: 'recommendations'
  title: string
  config: {
    curatedTopics: Array<number>
  }
}

export interface KnowledgeBaseHomepageElement extends HomepageElementInterface {
  type: 'knowledge_base'
  title: string
  config: {
    borderRadius: number
    enableBorder: boolean
    itemsPerContainer: number
    isVisibleDesktop: boolean
    isKnowledgeBase: boolean
  }
}

export interface TagCloudHomepageElement extends HomepageElementInterface {
  type: 'tag_cloud',
  title: string,
  config: {
    amount: number
  }
}

export interface CategoryAndContentStreamHomepageElement extends HomepageElementInterface {
  type: 'category_and_topics_stream'
  config: {
    tabs: Array<Tab>
  }
}

export interface QuicklinksHomepageElement extends HomepageElementInterface {
  type: 'quick_links'
  title: string
  config: {
    borderRadius: number
    enableBorder: boolean
    quickLinks: Array<{
      id: string
      title: string
      description: string
      openNewTab: boolean
      iconSrc: null | string
      imageSrc: null | string
      url: string
    }>
  }
}

export interface CategoryAndContentStreamOnlyHomepageElement extends HomepageElementInterface {
  type: 'category_and_topics_stream_only'
  config: {
    tabs: Array<Tab>
  }
}

export interface Container21HomepageElement extends HomepageElementInterface {
  type: 'container_2_1',
  config: {
    column_1: Array<HomepageElement>,
    column_2: Array<HomepageElement>,
  }
}

export interface LeaderboardPointsHomepageElement extends HomepageElementInterface {
  type: 'leaderboard_points'
  title: string
  config: {
    maxResults: number
    excludeUserRole: Array<string>
    timeRange: Array<TimeRanges>
    emptyMessage: string
    isPreviewEmptyMessage: boolean
  }
}

export interface BadgesHomepageElement extends HomepageElementInterface {
  type: 'badges',
  title: string
  config: {
    amount: number
  }
}

export interface SolvedTopicsHomepageElement extends HomepageElementInterface {
  type: 'solved_topics',
  title: string,
  config: {
    amount: number
  }
}

export interface CommunityStatisticsHomepageElement extends HomepageElementInterface {
  type: 'community_statistics',
  title: string,
  config: {
    total_topics: boolean,
    total_comments: boolean,
    total_users: boolean,
  }
}

export interface MostLikedHomepageElement extends HomepageElementInterface {
  type: 'most_liked',
  title: string,
  config: {
    amount: number,
    period: number
  }
}

export interface NotificationCardHomepageElement extends HomepageElementInterface {
  type: GenericHomepageElementTypes.announcement_card,
  title: string,
  config: {}
}

export interface StatisticsBarHomepageElement extends HomepageElementInterface {
  type: GenericHomepageElementTypes.statistics_bar,
  title: string,
  config: {}
}

export interface IntroductionBarHomepageElement extends HomepageElementInterface {
  type: GenericHomepageElementTypes.introduction_bar,
  title: string,
  config: {}
}

export interface CommunityCategoryElement extends HomepageElementInterface {
  type: 'community_category',
  title: string,
  config: {
    selectedTemplate: number
  }
}

export type HomepageElement =
  HTMLHomepageElement | GenericHomepageElement | QuicklinksHomepageElement | CommunityStatisticsHomepageElement |
  KnowledgeBaseHomepageElement | RecommendationsHomepageElement | CategoryAndContentStreamHomepageElement |
  HeroBannerHomepageElement | FeaturedTopicsElement | CategoryAndContentStreamOnlyHomepageElement |
  Container21HomepageElement | TagCloudHomepageElement | BadgesHomepageElement |
  SolvedTopicsHomepageElement | MostLikedHomepageElement | LeaderboardPointsHomepageElement |
  NotificationCardHomepageElement | CommunityCategoryElement | IntroductionBarHomepageElement |
  StatisticsBarHomepageElement

const SortableItem = SortableElement(({children}: {children: ComponentChild}) => children)

const SortableList = SortableContainer(({children}: {children: ComponentChild}) => children)

interface Props {
  items: Array<HomepageElement>
  onChange: (idx: number, data: Pick<HomepageElement, 'type' | 'config' | 'title'>) => void
  onReorder: (oldIndex: number, newIndex: number) => void
  onAdd: (el: HomepageElement) => void
  onRemove: (idx: number) => void
  onToggle: (idx: number) => void
}

export default class Homepage extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      allItems: props.items,
      isLoading: false,
      hasChanged: false
    }

    this.addWidget = this.addWidget.bind(this)
  }

  shouldComponentUpdate(nextProps: Props): boolean {
    return this.props.items !== nextProps.items
  }

  addWidget(): void {
    this.props.onAdd({
      id: getId(),
      type: 'html_widget',
      active: false,
      title: '',
      config: {
        html: ''
      }
    })

    setTimeout(() => {
      const widgets = document.querySelectorAll('.homepage-widget')

      function isHtmlElement(el: Element): el is HTMLElement {
        return 'offsetTop' in el
      }

      if (widgets.length < 2) return
      const prevToLastWidget = widgets[widgets.length - 2]

      if (!isHtmlElement(prevToLastWidget)) return
      window.scrollTo(0, prevToLastWidget.offsetTop)
    }, 100)
  }

  render(props: Props) {
    const homepageWidgets = props.items.map((item, idx) => {
      const switcher = <Switcher on={item.active} onChange={() => this.props.onToggle(idx)} />
      const dragHandle = <DragHandle />

      const wrap = (component: JSX.Element) => {
        return <SortableItem index={idx} key={item.id}>
          {component}
        </SortableItem>
      }

      switch (item.type) {
        case GenericHomepageElementTypes.statistics_bar: return wrap(<StatisticsBar dragHandle={dragHandle} switcher={switcher} />)
        case GenericHomepageElementTypes.introduction_bar: return wrap(<IntroductionBar dragHandle={dragHandle} switcher={switcher} />)
        case GenericHomepageElementTypes.feature_topics: return wrap(<FeaturedTopicsModeratorTags dragHandle={dragHandle} switcher={switcher} />)
        case 'hero_banner': return wrap(<Hero
          heroImage={item.config.heroImage}
          headline={item.config.headline}
          onChange={(config) => this.props.onChange(idx, {
            config: {
              heroImage: config.hero_image,
              headline: config.headline
            },
            type: item.type
          })}
          dragHandle={dragHandle}
          switcher={switcher}
        />)
        case 'quick_links': return wrap(<QuickLinksManager
          dragHandle={dragHandle}
          switcher={switcher}
          isActive={item.active}
          settings={{
            enableBorder: item.config.enableBorder,
            borderRadius: item.config.borderRadius
          }}
          quickLinks={item.config.quickLinks.map(link => ({
            id: link.id,
            title: link.title,
            description: link.description,
            openNewTab: link.openNewTab,
            url: link.url,
            iconSrc: link.iconSrc,
            imageSrc: link.imageSrc
          }))}
          title={item.title}
          onChange={(config) => this.props.onChange(idx, {
            title: config.title,
            config: {
              enableBorder: config.settings.enableBorder,
              borderRadius: config.settings.borderRadius,
              quickLinks: config.quickLinks.map(link => ({
                id: link.id,
                title: link.title,
                url: link.url,
                description: link.description,
                openNewTab: link.openNewTab,
                iconSrc: link.iconSrc,
                imageSrc: link.imageSrc
              }))
            },
            type: item.type
          })}
        />)
        case 'knowledge_base': return wrap(<KnowledgeBaseHomepageManager
          dragHandle={dragHandle}
          switcher={switcher}
          title={item.title}
          onChange={(config) => this.props.onChange(idx, {
            config: {
              title: config.title
            },
            title: config.title,
            type: item.type
          })}
        />)
        case 'recommendations': return wrap(<HomepageContentCurationManager
          dragHandle={dragHandle}
          switcher={switcher}
          isActive={item.active}
          title={item.title}
          curatedTopics={item.config.curatedTopics}
          onChange={(config) => this.props.onChange(idx, {
            config: {
              curatedTopics: config.curatedTopics
            },
            title: config.title,
            type: item.type
          })}
        />)
        case 'category_and_topics_stream': return wrap(<TabsConfigurator
          dragHandle={dragHandle}
          switcher={switcher}
          tabs={item.config.tabs}
          onChange={(config) => this.props.onChange(idx, {
            config: {
              tabs: config.tabs
            },
            type: item.type
          })}
        />)
        case 'html_widget': return wrap(<HTMLWidget
          dragHandle={dragHandle}
          switcher={switcher}
          onChange={(config) => this.props.onChange(idx, {
            config: {
              html: config.html
            },
            type: item.type,
            title: config.title
          })}
          deleteButtonAction={() => this.props.onRemove(idx)}
          title={item.title}
          content={item.config.html}
        />)
        case 'featured_topics': return wrap(<FeaturedTopics
          title={item.title}
          dragHandle={dragHandle}
          switcher={switcher}
          onChange={(config) => this.props.onChange(idx, {
            config: {
              title: config.title,
              topics: config.topics,
              firstTopicsNumber: config.firstTopicsNumber,
              buttonLabel: config.buttonLabel,
              buttonURL: config.buttonURL
            },
            title: config.title,
            type: item.type
          })}
          content={item.config}
        />)
        case GenericHomepageElementTypes.header: return <CommunityHeader switcher={switcher} />
        case GenericHomepageElementTypes.footer: return <CommunityFooter switcher={switcher} />
        case GenericHomepageElementTypes.navigation_bar: return <NavigationBar switcher={switcher} />

        default:
          return null
      }
    })

    const headerOptions = {
      title: 'Homepage',
      disableCancelButton: true,
      disableSaveButton: true,
      customButtons: [{
        title: 'Add HTML Widget',
        variant: 'primary',
        action: this.addWidget
      }]
    }

    return (
      <div>
        <Header {...headerOptions} />
        <SortableList
          useDragHandle
          onSortEnd={({oldIndex, newIndex}: {oldIndex: number, newIndex: number}) => this.props.onReorder(oldIndex, newIndex)}
        >
          <div>
            {homepageWidgets}
          </div>
        </SortableList>
      </div>
    )
  }
}
